import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { itSupportReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/itSupportExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/itSupportExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/itSupportExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function ItSupport() {
  const safeReviews = Array.isArray(itSupportReviews) ? itSupportReviews : [];
  const title = "IT / Ops Support — Troubleshooting, Documentation, Workflow Support";
  const description =
    "Practical IT and operations support for troubleshooting, documentation, tool setup, process cleanup, and keeping your business running more smoothly.";
  const canonical = "https://brainwavesolutions.net/services/it-support";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "IT / Ops Support", url: canonical },
    ]),
    buildServiceSchema({
      name: "IT / Ops Support",
      description,
      canonical,
      offerLabel: "$25–$500+",
      serviceType: "IT and operations support",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "IT support workflow documentation showing organized steps, tool setup notes, and support handoff details.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Operations support dashboard showing task tracking, issue notes, and workflow organization for team support.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Process documentation and operations support example with structured steps, assignments, and troubleshooting notes.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                IT and ops support that helps keep the day-to-day work moving
              </Typography>

              <p className="bws-gsl-lead">
                A lot of operational friction comes from small problems that never fully get cleaned up. Tools are set up
                halfway, processes depend on tribal knowledge, documentation is missing, and people keep working around
                the same issues instead of actually fixing them.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses with practical IT and operations support: troubleshooting, documentation, setup,
                process cleanup, and organizational fixes that make things easier to run. The goal is not to overengineer
                the solution — it is to reduce friction and make the work feel smoother.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Troubleshooting</span>
                <span className="bws-gsl-badge">Documentation &amp; SOPs</span>
                <span className="bws-gsl-badge">Tool setup</span>
                <span className="bws-gsl-badge">Process cleanup</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get IT / Ops help
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common IT / ops problems I help with</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Tools that are set up inconsistently or causing confusion</li>
                  <li>Processes that depend too much on memory instead of documentation</li>
                  <li>Recurring issues that keep slowing down the team</li>
                  <li>Workflow handoffs that are messy or unreliable</li>
                  <li>Operations that need clearer structure and support</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message describing what keeps breaking down, what feels disorganized, or what needs to run more smoothly.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Fix recurring friction</Typography>
              <Typography className="bws-gsl-stripText">
                Troubleshoot the issues that keep slowing people down and clean up the workflow around them.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Document the process</Typography>
              <Typography className="bws-gsl-stripText">
                Turn tribal knowledge into clearer SOPs, instructions, and handoff notes people can actually use.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Keep things moving</Typography>
              <Typography className="bws-gsl-stripText">
                Make tools, processes, and operational support more consistent and easier to maintain.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of support documentation, workflow cleanup, and process organization.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Troubleshooting recurring issues and workflow bottlenecks</li>
                    <li>Documentation, SOPs, and operational handoff notes</li>
                    <li>Tool setup, cleanup, and support</li>
                    <li>Process improvements and organizational fixes</li>
                    <li>Making day-to-day operations easier to support and maintain</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Small businesses that need practical support without a large IT team</li>
                    <li>Teams dealing with recurring operational friction</li>
                    <li>Businesses that need clearer documentation and processes</li>
                    <li>Anyone who wants tools and workflows to feel more organized and dependable</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Is this only for technical IT issues?</Typography>
                <Typography className="bws-gsl-faqA">
                  No. It can include practical troubleshooting, tool support, documentation, and process cleanup on the operations side too.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help if the issue is more about process than software?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. A lot of the value here is cleaning up the process around the tools, not just the tools themselves.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of what keeps going wrong, where the friction is, and what you want the workflow to feel like when it is working better.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help cleaning up an IT or ops problem?</Typography>
              <p className="bws-muted">
                Send a quick description of the issue, process, or recurring frustration you want to improve.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
